// eslint:disable-next-line:max-func-body-length
import * as React from 'react';
import * as navigate from 'app/area/shared/navigation';
import { MeatballMenu, PromptConfirmationModal, Link } from 'ui';
import { ids } from 'app/area/administration/constants';
import { ChangePersonId } from './change-id';
import { UserEditMenuComponentProps, UserEditMenuState } from './types';
import { DatabaseAccount } from 'app/area/administration/edit/user/user-edit-menu/types';
import { ChangePassword } from 'app/area/administration/edit/user/user-edit-menu/change-password';
import { ChangeDatabaseAccount } from 'app/area/administration/edit/user/user-edit-menu/change-db-account';
import { reAuthenticate } from 'app/infrastructure/auth';
import { getVeracityCompanyAdministrationLink, isVeracityEnabled } from 'app/infrastructure/hosting';
import { Tooltip } from 'ui/common/tooltip/tooltip';
import { maxPasswordLength } from 'app/area/administration/edit/constants';
import { replaceTemplateString } from 'app/infrastructure/platform';
import styles from './user-edit-menu.scss';

export class UserEditMenu extends React.PureComponent<UserEditMenuComponentProps, UserEditMenuState> {
  public state: Readonly<UserEditMenuState> = {
    changingPassword: false,
    changingId: false,
    databaseAccount: undefined
  };

  private handleDelete = () => {
    this.props.onDelete(this.props.id);
  };

  private handleDuplicate = () => {
    navigate.toDuplicateUser(this.props.id);
  };

  private handleEnablePasswordMode = () => {
    this.setState({ changingPassword: true });
  };

  private handleDisablePasswordMode = () => {
    this.setState({ changingPassword: false });
  };

  private handleSetPassword = async (password: string) => {
    const { id, onChangePassword } = this.props;
    onChangePassword(id, password, this.handleDisablePasswordMode);
  };

  private handleEnableDatabaseAccount = (databaseAccount: DatabaseAccount) => {
    this.setState({ databaseAccount });
  };

  private handleDisableDatabaseAccount = () => {
    this.setState({ databaseAccount: undefined });
  };

  private handleSaveDatabaseAccount = async (password: string) => {
    const { id, onSaveDatabaseAccount } = this.props;
    onSaveDatabaseAccount(id, password, this.handleDisableDatabaseAccount);
  };

  private handleResetDatabaseAccount = async (password: string) => {
    const { id, onResetDatabaseAccount } = this.props;
    onResetDatabaseAccount(id, password, this.handleDisableDatabaseAccount);
  };

  private handleDeleteDatabaseAccount = () => {
    this.props.onDeleteDatabaseAccount(this.props.id);
  };

  private enableChangingPersonId = () => {
    this.setState({ changingId: true });
  };

  private disableChangingPersonId = () => {
    this.setState({ changingId: false });
  };

  private changePersonId = (personId: string) => {
    const { id, loggedUserId, onChangePersonId, onLoadUser } = this.props;
    onChangePersonId(id, personId, () => {
      this.disableChangingPersonId();
      if (id === loggedUserId) {
        reAuthenticate();
      } else {
        onLoadUser(id);
      }
    });
  };

  private createDatabaseAccount = () => {
    this.handleEnableDatabaseAccount(DatabaseAccount.create);
  };

  private resetDatabaseAccount = () => {
    this.handleEnableDatabaseAccount(DatabaseAccount.reset);
  };

  // eslint:disable-next-line:max-func-body-length
  public render(): JSX.Element {
    const { id, personId, name, accessPerson, validation, clearValidation, fetching,
      optionsFetching, loggedUserId, canDuplicate, translation, technical, mergeUserItem } = this.props;
    const { changingPassword, changingId, databaseAccount } = this.state;

    if (id === undefined || !accessPerson || !(accessPerson.delete || accessPerson.edit)) {
      return null;
    }

    const veracityCompanyAdministrationLink = getVeracityCompanyAdministrationLink();
    const veracityEnabled = isVeracityEnabled();

    if (veracityEnabled && !veracityCompanyAdministrationLink) {
      return null;
    }

    if (veracityEnabled) {
      return <MeatballMenu className={styles.meatball} id={ids.list_search_item(id)} closeAfterClick={true}>
        <li>
          <Tooltip title={translation.veracity_redirect_info} className={styles.tooltip} narrow={true}>
            <Link componentId={ids.users.invite_veracity_user_button} url={veracityCompanyAdministrationLink}
              className="dropdown-item">
              {translation.delete_item}
            </Link>
          </Tooltip>
        </li>
      </MeatballMenu>;
    }

    return <>
      <MeatballMenu className={styles.meatball} id={ids.list_search_item(id)}>
        <>
          {canDuplicate && <li>
            <button type="button" className="dropdown-item" onClick={this.handleDuplicate} disabled={optionsFetching}>
              {translation.edit_role_duplicate_role}
            </button>
          </li>}

          {accessPerson.delete && technical && <li>
            <PromptConfirmationModal
              title={translation.technical_user_delete_title}
              description={replaceTemplateString(
                technical ? translation.technical_user_delete_description : translation.delete_user_desc,
                name)}
              continueText={technical ? translation.technical_user_delete_confirmation : translation.delete_item}
              cancelText={technical ? translation.go_back : translation.cancel}
              onContinue={this.handleDelete}>
              <button type="button" className="dropdown-item" disabled={optionsFetching}>
                {translation.delete_item}{accessPerson.delete}
              </button>
            </PromptConfirmationModal>
          </li>}

          {accessPerson.delete && !technical && <li>
            <button type="button" className="dropdown-item" onClick={mergeUserItem}>
              {translation.delete_item}
            </button>
          </li>}

          {accessPerson.edit && <li>
            {technical
              ? <PromptConfirmationModal
                title={translation.technical_user_update}
                description={translation.technical_user_update_description}
                continueText={translation.technical_user_update}
                cancelText={translation.go_back}
                onContinue={this.handleEnablePasswordMode}>
                <button type="button" className="dropdown-item" disabled={optionsFetching}>{translation.reset_password}</button>
              </PromptConfirmationModal>
              : <button type="button" className="dropdown-item" onClick={this.handleEnablePasswordMode} disabled={optionsFetching}>
                {translation.reset_password}
              </button>}
          </li>}

          {accessPerson.edit && !accessPerson.dbAccess.edit && <li>
            {technical
              ? <PromptConfirmationModal
                title={translation.technical_user_update}
                description={translation.technical_user_update_description}
                continueText={translation.technical_user_update}
                cancelText={translation.go_back}
                onContinue={this.enableChangingPersonId}>
                <button type="button" className="dropdown-item" disabled={optionsFetching}>{translation.change_user_id}</button>
              </PromptConfirmationModal>
              : <button type="button" className="dropdown-item" onClick={this.enableChangingPersonId} disabled={optionsFetching}>
                {translation.change_user_id}
              </button>}
          </li>}

          {accessPerson.dbAccess.add && <li>
            <button type="button" className="dropdown-item" onClick={this.createDatabaseAccount} disabled={optionsFetching}>
              {translation.database_user_create_button}
            </button>
          </li>}

          {accessPerson.dbAccess.edit && <li>
            <button type="button" className="dropdown-item" onClick={this.resetDatabaseAccount} disabled={optionsFetching}>
              {translation.database_user_reset_button}
            </button>
          </li>}

          {accessPerson.dbAccess.delete && <li>
            <PromptConfirmationModal
              title={translation.database_user_delete_modal_title}
              description={translation.database_user_delete_modal_desc}
              continueText={translation.database_user_delete_button}
              cancelText={translation.cancel}
              onContinue={this.handleDeleteDatabaseAccount}>
              <button type="button" className="dropdown-item" disabled={optionsFetching}>
                {translation.database_user_delete_button}
              </button>
            </PromptConfirmationModal>
          </li>}
        </>
      </MeatballMenu>

      {
        changingPassword && <ChangePassword
          onClose={this.handleDisablePasswordMode}
          setPassword={this.handleSetPassword}
          validation={validation.password}
          clearValidation={clearValidation}
          textTitle={translation.change_password}
          textPassword={translation.password}
          textConfirmPassword={translation.confirm_password}
          textDoneButton={translation.change}
          textCancel={translation.cancel}
          textMustBeEqual={translation.must_be_equal}
          textPasswordHint={translation.password_policy_user_account}
          fetching={fetching}
          maxLength={maxPasswordLength}
        />
      }

      {
        changingId && <ChangePersonId
          personId={personId}
          editingSelf={id === loggedUserId}
          onClose={this.disableChangingPersonId}
          setPersonId={this.changePersonId}
          validation={validation.personId}
          clearValidation={clearValidation}
          fetching={fetching}
          textTitle={translation.change_user_id}
          textOldPersonId={translation.old_user_id}
          textNewPersonId={translation.new_user_id}
          textDoneButton={translation.change}
          textCancel={translation.cancel}
          textDisclaimer={translation.change_id_disclaimer}
        />
      }

      {
        databaseAccount === DatabaseAccount.create &&
        <ChangeDatabaseAccount
          title={translation.database_user_create_modal_title}
          textInfo={translation.database_create_user_description}
          onSave={this.handleSaveDatabaseAccount}
          onClose={this.handleDisableDatabaseAccount}
          {...this.props} />
      }

      {
        databaseAccount === DatabaseAccount.reset &&
        <ChangeDatabaseAccount
          title={translation.database_user_reset_modal_title}
          onSave={this.handleResetDatabaseAccount}
          onClose={this.handleDisableDatabaseAccount}
          {...this.props} />
      }
    </>;
  }
}
